import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckCircle2, FileX } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge, Card, EmptyState, Skeleton } from "../../components/ui/card";
import { client, orpc } from "../../lib/api";
import { errorMessage, formatDate, formatDateTime } from "../../lib/utils";
import { AdminPageHeader } from "./layout";

const STATUS = {
  cancelled: { label: "Automatisch gekündigt", tone: "success" },
  open: { label: "Zu bearbeiten", tone: "warning" },
  none: { label: "Kein laufendes Abo", tone: "neutral" },
} as const;

/**
 * Submissions of "Verträge hier kündigen". Open ones could not be matched to a
 * subscription (unknown address, several organizations, extraordinary): cancel
 * in the Stripe dashboard, answer the customer, then mark them as handled.
 */
export function AdminCancellationsPage() {
  const queryClient = useQueryClient();
  const [onlyOpen, setOnlyOpen] = useState(true);
  const list = useQuery(orpc.admin.cancellations.list.queryOptions({ input: { onlyOpen } }));

  const resolve = async (id: string) => {
    try {
      await client.admin.cancellations.resolve({ id });
      await queryClient.invalidateQueries({ queryKey: orpc.admin.key() });
      toast.success("Als erledigt markiert");
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <>
      <AdminPageHeader
        title="Kündigungen"
        description="Eingänge über „Verträge hier kündigen“. Offene ließen sich keinem Abo sicher zuordnen: im Stripe-Dashboard kündigen, der Person antworten, dann als erledigt markieren."
      />
      <div className="mb-4 flex gap-1">
        {([true, false] as const).map((value) => (
          <Button
            key={String(value)}
            size="sm"
            variant={onlyOpen === value ? "primary" : "secondary"}
            onClick={() => setOnlyOpen(value)}
          >
            {value ? "Offen" : "Alle"}
          </Button>
        ))}
      </div>
      {!list.data ? (
        <Skeleton className="h-40" />
      ) : list.data.length === 0 ? (
        <EmptyState
          icon={<FileX />}
          title={onlyOpen ? "Nichts zu bearbeiten" : "Noch keine Kündigungen"}
          description="Neue Eingänge ohne Zuordnung erscheinen hier und als Warnung."
        />
      ) : (
        <Card className="divide-y divide-zinc-100">
          {list.data.map((item) => (
            <div key={item.id} className="flex flex-wrap items-start gap-4 p-4 text-sm">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="flex flex-wrap items-center gap-2 font-medium">
                  {item.name} · {item.email}
                  <Badge tone={item.handledAt ? "neutral" : STATUS[item.status].tone}>
                    {item.handledAt ? "Erledigt" : STATUS[item.status].label}
                  </Badge>
                  {item.kind === "extraordinary" ? (
                    <Badge tone="danger">Außerordentlich</Badge>
                  ) : null}
                </p>
                <p className="text-zinc-600">
                  Eingegangen {formatDateTime(item.createdAt)}
                  {item.organizationName ? ` · Organisation „${item.organizationName}“` : ""}
                  {item.endsAt ? ` · endet am ${formatDate(item.endsAt)}` : ""}
                </p>
                {item.reason ? (
                  <p className="whitespace-pre-line text-zinc-700">Grund: {item.reason}</p>
                ) : null}
                {item.organizationId ? (
                  <Link
                    to="/admin/orgs/$orgId"
                    params={{ orgId: item.organizationId }}
                    className="text-brand-700 underline"
                  >
                    Organisation öffnen
                  </Link>
                ) : null}
              </div>
              {item.handledAt || item.status !== "open" ? null : (
                <Button size="sm" variant="secondary" onClick={() => void resolve(item.id)}>
                  <CheckCircle2 /> Erledigt
                </Button>
              )}
            </div>
          ))}
        </Card>
      )}
    </>
  );
}
